import React, { useState, useEffect } from 'react';
import { Trash2, Loader2, Car } from 'lucide-react';
import { vehiclesAPI } from '../../services/api';

const FUELS = [
  { value: 'essence', label: 'Essence' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'hybride', label: 'Hybride' },
  { value: 'essence/électrique', label: 'Essence/électrique' },
  { value: 'diesel/électrique', label: 'Diesel/électrique' },
  { value: 'essence/gpl', label: 'Essence / gaz combustible liquéfié (GPL)' },
  { value: 'essence/éthanol', label: 'Essence/éthanol' },
  { value: 'essence/éthanol/gaz', label: 'Essence / éthanol / gaz' },
  { value: 'électrique', label: 'Électrique' },
  { value: 'gpl', label: 'GPL' },
];


const CompatRuleEditor = ({ rule, index, brands, onChange, onRemove }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [engines, setEngines] = useState([]);
  const [engineQuery, setEngineQuery] = useState('');

  const modelsKey = (rule.models || []).join('|');
  useEffect(() => {
    if (!rule.brand) { setEngines([]); return; }
    let cancelled = false;
    vehiclesAPI.getBrandEngines(rule.brand, rule.models || [])
      .then(data => { if (!cancelled) setEngines(data); })
      .catch(() => { if (!cancelled) setEngines([]); });
    return () => { cancelled = true; };
  }, [rule.brand, modelsKey]);

  const visibleEngines = engines.filter(e => {
    if (rule.fuels?.length && !rule.fuels.includes(e.fuel)) return false;
    if (!engineQuery) return true;
    return e.name.toLowerCase().includes(engineQuery.toLowerCase());
  });

  useEffect(() => {
    if (!rule.brand) { setModels([]); return; }
    let cancelled = false;
    setLoading(true);
    vehiclesAPI.getModels(rule.brand)
      .then(data => { if (!cancelled) setModels(data); })
      .catch(() => { if (!cancelled) setModels([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rule.brand]);

  const toggle = (key, value) => {
    const list = rule[key] || [];
    onChange({ ...rule, [key]: list.includes(value) ? list.filter(v => v !== value) : [...list, value] });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50" data-testid={`compat-rule-${index}`}>
      <div className="flex items-center gap-3 mb-3">
        <Car size={16} className="text-orange-500" />
        <select
          data-testid={`compat-rule-${index}-brand`}
          value={rule.brand}
          onChange={(e) => onChange({ ...rule, brand: e.target.value, models: [], engine_names: [] })}
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="">Choisir une marque</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <button
          type="button"
          data-testid={`compat-rule-${index}-remove`}
          onClick={onRemove}
          className="p-2 text-red-500 hover:bg-red-50 rounded"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {rule.brand && (
        <>
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-600">
                Modèles {rule.models?.length ? `(${rule.models.length} sélectionnés)` : '(tous les modèles)'}
              </p>
              {rule.models?.length > 0 && (
                <button type="button" onClick={() => onChange({ ...rule, models: [] })} className="text-xs text-blue-600 hover:underline">
                  Tous
                </button>
              )}
            </div>
            {loading ? (
              <Loader2 size={16} className="animate-spin text-orange-500" />
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {models.map(m => {
                  const active = rule.models?.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      data-testid={`compat-rule-${index}-model-${m}`}
                      onClick={() => toggle('models', m)}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                        active ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">
              Carburants {rule.fuels?.length ? '' : '(tous)'}
            </p>
            <div className="flex flex-wrap gap-3">
              {FUELS.map(f => (
                <label key={f.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`compat-rule-${index}-fuel-${f.value}`}
                    checked={rule.fuels?.includes(f.value) || false}
                    onChange={() => toggle('fuels', f.value)}
                    className="rounded border-gray-300 text-orange-500"
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-xs font-medium text-gray-600">
                Motorisations {rule.engine_names?.length ? `(${rule.engine_names.length} sélectionnées)` : '(toutes les motorisations)'}
              </p>
              <div className="flex items-center gap-2">
                {rule.engine_names?.length > 0 && (
                  <button type="button" data-testid={`compat-rule-${index}-engines-clear`} onClick={() => onChange({ ...rule, engine_names: [] })} className="text-xs text-blue-600 hover:underline">Toutes</button>
                )}
                <input
                  data-testid={`compat-rule-${index}-engine-search`}
                  value={engineQuery}
                  onChange={(e) => setEngineQuery(e.target.value)}
                  placeholder="Filtrer (ex : TCe 100)"
                  className="border border-gray-300 rounded px-2 py-1 text-xs w-36 bg-white"
                />
              </div>
            </div>
            {engines.length === 0 ? (
              <p className="text-xs text-gray-400">Aucune motorisation connue pour cette sélection.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1" data-testid={`compat-rule-${index}-engines`}>
                {visibleEngines.map(e => {
                  const active = rule.engine_names?.includes(e.name);
                  return (
                    <button
                      key={e.name}
                      type="button"
                      data-testid={`compat-rule-${index}-engine-${e.name}`}
                      onClick={() => toggle('engine_names', e.name)}
                      title={`${e.fuel} · ${e.power} ch · ${e.models.join(', ')}`}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${active ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`}
                    >
                      {e.name} <span className={active ? 'text-blue-200' : 'text-gray-400'}>· {e.power} ch</span>
                    </button>
                  );
                })}
                {visibleEngines.length === 0 && <p className="text-xs text-gray-400">Aucune motorisation ne correspond au filtre.</p>}
              </div>
            )}
            {rule.engine_names?.some(n => !engines.find(e => e.name === n)) && (
              <p className="text-[11px] text-amber-600 mt-1">Certaines motorisations sélectionnées ne correspondent plus aux modèles choisis : {rule.engine_names.filter(n => !engines.find(e => e.name === n)).join(', ')}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CompatRuleEditor;
